import React, { useEffect, useState, useCallback } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchAllBehaviorConfigs,
  createBehaviorConfig,
} from "../../redux/actions/behaviorConfigActions";
import { RootState, AppDispatch } from "../../redux/store";
import { format } from "date-fns";

const BehaviorConfigsTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { behaviorConfigs, loading, error } = useSelector(
    (state: RootState) => state.behaviorConfigs
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newBehaviorName, setNewBehaviorName] = useState("");
  const [newDemoMode, setNewDemoMode] = useState("");

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch all behavior configs when the component mounts
  useEffect(() => {
    dispatch(fetchAllBehaviorConfigs());
  }, [dispatch]);

  const handleViewBehaviorConfig = useCallback(
    (id: string) => {
      navigate(`/dashboard/behavior-configs/${id}`);
    },
    [navigate]
  );

  const handleCreateBehaviorConfig = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewBehaviorName("");
    setNewDemoMode("");
  };

  const handleCreateBehaviorConfigSubmit = () => {
    if (!newBehaviorName) {
      toast.error("Behavior Name is required");
      return;
    }

    const behaviorConfigData = {
      behavior_name: newBehaviorName,
      demo_mode: newDemoMode || undefined,
    };

    dispatch(createBehaviorConfig(behaviorConfigData))
      .unwrap()
      .then((config) => {
        toast.success("Behavior Config created successfully");
        handleDialogClose();
        navigate(`/dashboard/behavior-configs/${config.id}`, {
          state: { toastMessage: "Behavior Config created successfully" },
        });
      })
      .catch((error) => {
        toast.error(`Failed to create behavior config: ${error.message}`);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Config ID",
      flex: 1.5,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "behavior_name",
      headerName: "Behavior Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "demo_mode",
      headerName: "Demo Mode",
      flex: 0.5,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        return params || "None";
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        return params ? format(new Date(params), "yyyy-MM-dd HH:mm:ss") : "";
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        return params
          ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
          : "Never";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewBehaviorConfig(params.row.id)}
        >
          View
        </Button>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  if (loading && behaviorConfigs.length === 0) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateBehaviorConfig}
          size="medium"
          sx={{ fontSize: "0.9rem", padding: "6px 16px" }}
        >
          Create Behavior Config
        </Button>
      </Box>

      <Box sx={{ height: "70vh", width: "100%" }}>
        <DataGrid
          rows={behaviorConfigs}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "created_at", sort: "desc" }],
            },
          }}
        />
      </Box>

      {/* Dialog for creating a new behavior config */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create New Behavior Config</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the behavior configuration details. A unique ID will be
            generated automatically.
          </Typography>
          <TextField
            label="Behavior Name"
            value={newBehaviorName}
            onChange={(e) => setNewBehaviorName(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="The name of the behavior (e.g., 'therapy', 'investor_demo_behavior')"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Demo Mode</InputLabel>
            <Select
              value={newDemoMode}
              onChange={(e) => setNewDemoMode(e.target.value)}
              label="Demo Mode"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="travel">Travel</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
              <MenuItem value="hr">HR</MenuItem>
              <MenuItem value="non_hosted_investor">
                Non-Hosted Investor
              </MenuItem>
              <MenuItem value="non_demo">
                Non-demo (default)
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleCreateBehaviorConfigSubmit}
            color="primary"
            disabled={!newBehaviorName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BehaviorConfigsTab;
